import { AlertIcon } from '@approvalmax/ui';
import {
    Alert,
    Box,
    Button,
    Collapse,
    DatePicker,
    Flex,
    Form,
    Grid,
    Label,
    NumberField,
    Text,
    TextField,
} from '@approvalmax/ui/src/components';
import { numberHelpers } from '@approvalmax/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { memo, SyntheticEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useUnmount } from 'react-use';
import { noValue } from 'shared/constants';
import { GetNetSuiteBillPaymentBills } from 'shared/data/webApp/v2';

import { initialFilterValues } from './BillsFilter.constants';
import { useMinDateToValue } from './BillsFilter.hooks';
import { messages } from './BillsFilter.messages';
import { schema } from './BillsFilter.schema';
import { BillsFilterProps, FormValues } from './BillsFilter.types';

export const BillsFilter = memo<BillsFilterProps>((props) => {
    const { currency, isLoading, setFilters } = props;

    const form = useForm<FormValues>({
        defaultValues: {
            ...initialFilterValues,
        },
        resolver: zodResolver(schema),
    });

    const dateFrom = form.watch('dateFrom');
    const dueDateFrom = form.watch('dueDateFrom');

    const minDateTo = useMinDateToValue(dateFrom);
    const minDueDateTo = useMinDateToValue(dueDateFrom);

    const applyFilters = useCallback(
        (event?: SyntheticEvent<HTMLFormElement>) => {
            event?.preventDefault();

            const handleSubmit = form.handleSubmit((filterData) => {
                const transfer: Partial<GetNetSuiteBillPaymentBills['params']['query']> = {};

                if (filterData.reference) {
                    transfer.reference = filterData.reference;
                }

                if (numberHelpers.isNumber(filterData.amountDueFrom)) {
                    transfer.amountDueFrom = filterData.amountDueFrom;
                }

                if (numberHelpers.isNumber(filterData.amountDueTo)) {
                    transfer.amountDueTo = filterData.amountDueTo;
                }

                if (numberHelpers.isNumber(filterData.originalAmountFrom)) {
                    transfer.originalAmountFrom = filterData.originalAmountFrom;
                }

                if (numberHelpers.isNumber(filterData.originalAmountTo)) {
                    transfer.originalAmountTo = filterData.originalAmountTo;
                }

                if (filterData.dateFrom) {
                    transfer.dateFrom = filterData.dateFrom.toISOString();
                }

                if (filterData.dateTo) {
                    transfer.dateTo = filterData.dateTo.toISOString();
                }

                if (filterData.dueDateFrom) {
                    transfer.dueDateFrom = filterData.dueDateFrom.toISOString();
                }

                if (filterData.dueDateTo) {
                    transfer.dueDateTo = filterData.dueDateTo.toISOString();
                }

                setFilters(transfer);
            });

            handleSubmit();
        },
        [form, setFilters]
    );

    const onEnter = useCallback(() => applyFilters(), [applyFilters]);

    const resetFilters = useCallback(() => {
        form.reset();
        setFilters({});
    }, [form, setFilters]);

    useUnmount(() => {
        resetFilters();
    });

    return (
        <Box spacing='16 0 20'>
            <Form form={form} onSubmit={applyFilters}>
                <Collapse
                    headerInRow
                    bordered
                    name={messages.title}
                    spacing='0 24 16 24'
                    radius='small'
                    headerJustifyContent='start'
                    headerContent={
                        <Box width='100%'>
                            <Alert color='transparent' size='small' startIcon={<AlertIcon />}>
                                {messages.note}
                            </Alert>
                        </Box>
                    }
                >
                    <Grid gap={16}>
                        <Grid gridTemplateColumns='1fr 1fr 1fr' columnGap='16px' rowGap='20px'>
                            <Grid rowGap={4}>
                                <Label htmlFor='bills-filter-reference-field' size='small'>
                                    {messages.referenceFieldLabel}
                                </Label>

                                <TextField
                                    id='bills-filter-reference-field'
                                    name='reference'
                                    placeholder={messages.referenceFieldPlaceholder}
                                    maxLength={255}
                                    size='small'
                                />
                            </Grid>

                            <Grid rowGap={4}>
                                <Label htmlFor='bills-filter-amountDueFrom-field' size='small'>
                                    {messages.amountDueFieldLabel({ currency: currency.text })}
                                </Label>

                                <Flex spacing='4' alignItems='center' wrap={false}>
                                    <NumberField
                                        id='bills-filter-amountDueFrom-field'
                                        name='amountDueFrom'
                                        placeholder={messages.fromFieldLabel}
                                        size='small'
                                        onEnter={onEnter}
                                    />

                                    <Text font='label' fontSize='small'>
                                        {noValue}
                                    </Text>

                                    <NumberField
                                        name='amountDueTo'
                                        placeholder={messages.toFieldLabel}
                                        size='small'
                                        onEnter={onEnter}
                                    />
                                </Flex>
                            </Grid>

                            <Grid rowGap={4}>
                                <Label htmlFor='bills-filter-originalAmountFrom-field' size='small'>
                                    {messages.originalAmountFieldLabel({ currency: currency.text })}
                                </Label>

                                <Flex spacing='4' alignItems='center' wrap={false}>
                                    <NumberField
                                        id='bills-filter-originalAmountFrom-field'
                                        name='originalAmountFrom'
                                        placeholder={messages.fromFieldLabel}
                                        size='small'
                                        onEnter={onEnter}
                                    />

                                    <Text font='label' fontSize='small'>
                                        {noValue}
                                    </Text>

                                    <NumberField
                                        name='originalAmountTo'
                                        placeholder={messages.toFieldLabel}
                                        size='small'
                                        onEnter={onEnter}
                                    />
                                </Flex>
                            </Grid>

                            <Grid gridColumn={2} rowGap={4} gridAutoRows='max-content'>
                                <Label htmlFor='bills-filter-dateFrom-field' size='small'>
                                    {messages.dateFieldLabel}
                                </Label>

                                <Flex spacing='4' wrap={false}>
                                    <DatePicker
                                        id='bills-filter-dateFrom-field'
                                        name='dateFrom'
                                        placeholder={messages.fromFieldLabel}
                                        width='100%'
                                        size='small'
                                    />

                                    <Flex height={28} alignItems='center'>
                                        <Text font='label' fontSize='small'>
                                            {noValue}
                                        </Text>
                                    </Flex>

                                    <DatePicker
                                        name='dateTo'
                                        minDate={minDateTo}
                                        placeholder={messages.toFieldLabel}
                                        width='100%'
                                        size='small'
                                    />
                                </Flex>
                            </Grid>

                            <Grid gridColumn={3} rowGap={4} gridAutoRows='max-content'>
                                <Label htmlFor='bills-filter-dueDateFrom-field' size='small'>
                                    {messages.dueDateFieldLabel}
                                </Label>

                                <Flex spacing='4' wrap={false}>
                                    <DatePicker
                                        id='bills-filter-dueDateFrom-field'
                                        name='dueDateFrom'
                                        placeholder={messages.fromFieldLabel}
                                        width='100%'
                                        size='small'
                                    />

                                    <Flex height={28} alignItems='center'>
                                        <Text font='label' fontSize='small'>
                                            {noValue}
                                        </Text>
                                    </Flex>

                                    <DatePicker
                                        name='dueDateTo'
                                        minDate={minDueDateTo}
                                        placeholder={messages.toFieldLabel}
                                        width='100%'
                                        size='small'
                                    />
                                </Flex>
                            </Grid>
                        </Grid>

                        <Flex justifyContent='end'>
                            <Button color='blue10' size='small' onClick={resetFilters} disabled={isLoading}>
                                {messages.resetButtonText}
                            </Button>

                            <Button type='submit' size='small' color='midnight40' progress={isLoading}>
                                {messages.applyButtonText}
                            </Button>
                        </Flex>
                    </Grid>
                </Collapse>
            </Form>
        </Box>
    );
});

BillsFilter.displayName = 'BillsFilter';
